import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Fade } from '@mui/material';
import { Campaign as CampaignIcon } from '@mui/icons-material';

const DEFAULT_STYLE = {
  accentColor: '#1976d2',
  headerBg: '#e3f2fd',
  headerText: '#1565c0',
  borderColor: '#1976d2'
};

/**
 * Rotating kiosk announcement banner (public config from /api/kiosk/announcements).
 */
const KioskAnnouncementBanner = ({ config }) => {
  const items = useMemo(
    () => (Array.isArray(config?.items) ? config.items.filter((item) => item?.text) : []),
    [config?.items]
  );
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  const style = { ...DEFAULT_STYLE, ...(config?.style || {}) };
  const label = config?.label || 'ANNOUNCEMENTS';
  const rotationMs = Math.max(4000, (Number(config?.rotationSeconds) || 8) * 1000);

  useEffect(() => {
    setIndex(0);
    setVisible(true);
  }, [items]);

  useEffect(() => {
    if (items.length <= 1) return undefined;

    const interval = setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((prev) => (prev + 1) % items.length);
        setVisible(true);
      }, 350);
    }, rotationMs);

    return () => clearInterval(interval);
  }, [items, rotationMs]);

  if (!config?.enabled || !items.length) return null;

  const current = items[index] || items[0];

  return (
    <Box
      role="region"
      aria-label={label}
      sx={{
        width: '100%',
        maxWidth: 720,
        mb: 2.5,
        border: `2px solid ${style.borderColor}`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: 1
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          minHeight: 72
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1.25,
            bgcolor: style.headerBg,
            borderRight: '1px solid',
            borderColor: 'divider',
            flexShrink: 0
          }}
        >
          <CampaignIcon sx={{ color: style.accentColor, fontSize: 28 }} />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              letterSpacing: 0.6,
              color: style.headerText,
              whiteSpace: 'nowrap'
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1.25,
            minWidth: 0,
            borderTop: `4px solid ${style.accentColor}`
          }}
        >
          <Fade in={visible} timeout={350}>
            <Typography
              variant="body1"
              aria-live="polite"
              sx={{
                fontWeight: 500,
                lineHeight: 1.45,
                width: '100%'
              }}
            >
              {current.text}
            </Typography>
          </Fade>
        </Box>
      </Box>
      {items.length > 1 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 0.75,
            py: 0.75,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50'
          }}
        >
          {items.map((item, i) => (
            <Box
              key={item.id || i}
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: i === index ? style.accentColor : 'grey.400',
                transition: 'background-color 0.25s'
              }}
            />
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

export default KioskAnnouncementBanner;
